/**
 * Add Step Screen
 * Form to add a new step to a speurtocht
 */

import React, { useState, useEffect, useCallback } from "react";
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  TextInput,
  Switch,
  Alert,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { useRouter, useLocalSearchParams } from "expo-router";
import { useThemeColors } from "@/providers/ThemeProvider";
import {
  useSpeurttochtAdmin,
  Speurtocht,
  StepType,
  STEP_TYPE_CONFIG,
  STEP_TYPE_COLORS,
} from "@/modules/speurtocht";
import { LottiePicker } from "@/components/LottiePicker";

export default function AddStepScreen() {
  const router = useRouter();
  const { id } = useLocalSearchParams<{ id: string }>();
  const colors = useThemeColors();
  const { fetchSpeurtocht, addStep, isSubmitting, error } = useSpeurttochtAdmin();

  // State
  const [isLoading, setIsLoading] = useState(true);
  const [speurtocht, setSpeurtocht] = useState<Speurtocht | null>(null);

  // Form state
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [stepType, setStepType] = useState<StepType>("location");
  const [hint, setHint] = useState("");
  const [completionLottie, setCompletionLottie] = useState<string | null>(null);

  // Step-type specific config
  const [locationAddress, setLocationAddress] = useState("");
  const [locationLatitude, setLocationLatitude] = useState("");
  const [locationLongitude, setLocationLongitude] = useState("");
  const [locationRadius, setLocationRadius] = useState("50");
  const [locationShowDistance, setLocationShowDistance] = useState(true);

  const [puzzleQuestion, setPuzzleQuestion] = useState("");
  const [puzzleAnswer, setPuzzleAnswer] = useState("");
  const [puzzleCaseSensitive, setPuzzleCaseSensitive] = useState(false);

  const [wordAnswer, setWordAnswer] = useState("");
  const [wordCaseSensitive, setWordCaseSensitive] = useState(false);

  const [photoInstructions, setPhotoInstructions] = useState("");
  const [photoMinCount, setPhotoMinCount] = useState("1");

  const [qrCodeValue, setQrCodeValue] = useState("");

  /**
   * Load speurtocht
   */
  const loadSpeurtocht = useCallback(async () => {
    if (!id) return;

    const data = await fetchSpeurtocht(id);
    setSpeurtocht(data);
    setIsLoading(false);
  }, [id, fetchSpeurtocht]);

  useEffect(() => {
    loadSpeurtocht();
  }, [loadSpeurtocht]);

  /**
   * Build config based on step type
   */
  const buildConfig = () => {
    switch (stepType) {
      case "location":
        return {
          address: locationAddress.trim() || undefined,
          latitude: locationLatitude ? parseFloat(locationLatitude) : undefined,
          longitude: locationLongitude ? parseFloat(locationLongitude) : undefined,
          radius_meters: parseInt(locationRadius) || 50,
          show_distance: locationShowDistance,
        };
      case "puzzle":
        return {
          question: puzzleQuestion.trim(),
          correct_answer: puzzleAnswer.trim(),
          case_sensitive: puzzleCaseSensitive,
        };
      case "word":
        return {
          correct_answer: wordAnswer.trim(),
          case_sensitive: wordCaseSensitive,
        };
      case "photo":
        return {
          instructions: photoInstructions.trim() || undefined,
          min_photos: parseInt(photoMinCount) || 1,
        };
      case "qr_code":
        return {
          expected_value: qrCodeValue.trim(),
        };
      default:
        return {};
    }
  };

  /**
   * Validate form
   */
  const validateForm = (): boolean => {
    if (!title.trim()) {
      Alert.alert("Fout", "Voer een titel in voor de stap");
      return false;
    }

    switch (stepType) {
      case "location":
        if (!locationAddress.trim() && (!locationLatitude || !locationLongitude)) {
          Alert.alert("Fout", "Voer een adres of coördinaten in");
          return false;
        }
        break;
      case "puzzle":
        if (!puzzleQuestion.trim() || !puzzleAnswer.trim()) {
          Alert.alert("Fout", "Voer een vraag en antwoord in");
          return false;
        }
        break;
      case "word":
        if (!wordAnswer.trim()) {
          Alert.alert("Fout", "Voer het juiste antwoord in");
          return false;
        }
        break;
      case "qr_code":
        if (!qrCodeValue.trim()) {
          Alert.alert("Fout", "Voer de QR code waarde in");
          return false;
        }
        break;
    }

    return true;
  };

  /**
   * Handle create
   */
  const handleCreate = async () => {
    if (!id || !validateForm()) return;

    const result = await addStep({
      speurtocht_id: id,
      title: title.trim(),
      description: description.trim() || undefined,
      step_type: stepType,
      config: buildConfig(),
      hint: hint.trim() || undefined,
      lottie_animation: completionLottie || undefined,
    });

    if (result) {
      router.back();
    }
  };

  if (isLoading) {
    return (
      <SafeAreaView
        className="flex-1 items-center justify-center"
        style={{ backgroundColor: colors.background }}
      >
        <ActivityIndicator size="large" color="#10B981" />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView className="flex-1" style={{ backgroundColor: colors.background }}>
      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : "height"}
        className="flex-1"
      >
        {/* Header */}
        <View className="px-4 py-4 flex-row items-center justify-between">
          <View className="flex-row items-center">
            <TouchableOpacity
              onPress={() => router.back()}
              className="w-10 h-10 rounded-full items-center justify-center mr-3"
              style={{ backgroundColor: colors.surface }}
              accessibilityRole="button"
              accessibilityLabel="Terug"
            >
              <Ionicons name="close" size={22} color={colors.text} />
            </TouchableOpacity>
            <Text className="text-xl font-bold" style={{ color: colors.text }}>
              Nieuwe Stap
            </Text>
          </View>

          <TouchableOpacity
            onPress={handleCreate}
            disabled={isSubmitting || !title.trim()}
            className="px-4 py-2 rounded-xl flex-row items-center"
            style={{
              backgroundColor: title.trim() ? "#10B981" : colors.surfaceSecondary,
              opacity: isSubmitting ? 0.7 : 1,
            }}
          >
            {isSubmitting ? (
              <ActivityIndicator size="small" color="#FFFFFF" />
            ) : (
              <>
                <Ionicons
                  name="checkmark"
                  size={18}
                  color={title.trim() ? "#FFFFFF" : colors.textTertiary}
                />
                <Text
                  className="ml-1 font-bold"
                  style={{ color: title.trim() ? "#FFFFFF" : colors.textTertiary }}
                >
                  Toevoegen
                </Text>
              </>
            )}
          </TouchableOpacity>
        </View>

        <ScrollView className="flex-1 px-4" showsVerticalScrollIndicator={false}>
          {/* Error Message */}
          {error && (
            <View
              className="p-3 rounded-xl mb-4"
              style={{ backgroundColor: colors.error + "20" }}
            >
              <Text style={{ color: colors.error }}>{error}</Text>
            </View>
          )}

          {/* Step Type Selection */}
          <Text
            className="text-sm font-semibold mb-2 uppercase"
            style={{ color: colors.textSecondary }}
          >
            Type Stap
          </Text>
          <View
            className="rounded-xl p-4 mb-6"
            style={{ backgroundColor: colors.surface }}
          >
            <View className="flex-row flex-wrap">
              {(Object.keys(STEP_TYPE_CONFIG) as StepType[]).map((type) => {
                const config = STEP_TYPE_CONFIG[type];
                const color = STEP_TYPE_COLORS[type];
                const isSelected = stepType === type;

                return (
                  <TouchableOpacity
                    key={type}
                    onPress={() => setStepType(type)}
                    className="w-1/2 p-2"
                  >
                    <View
                      className="p-3 rounded-xl items-center"
                      style={{
                        backgroundColor: isSelected ? `${color}25` : colors.surfaceSecondary,
                        borderWidth: isSelected ? 2 : 0,
                        borderColor: color,
                      }}
                    >
                      <View
                        className="w-10 h-10 rounded-full items-center justify-center mb-2"
                        style={{ backgroundColor: `${color}20` }}
                      >
                        <Ionicons
                          name={config.icon as any}
                          size={20}
                          color={color}
                        />
                      </View>
                      <Text
                        className="font-medium text-center"
                        style={{ color: isSelected ? color : colors.text }}
                      >
                        {config.label}
                      </Text>
                    </View>
                  </TouchableOpacity>
                );
              })}
            </View>
          </View>

          {/* Basic Info */}
          <Text
            className="text-sm font-semibold mb-2 uppercase"
            style={{ color: colors.textSecondary }}
          >
            Basis Informatie
          </Text>
          <View
            className="rounded-xl p-4 mb-6"
            style={{ backgroundColor: colors.surface }}
          >
            <View className="mb-4">
              <Text className="text-sm font-medium mb-1" style={{ color: colors.text }}>
                Titel *
              </Text>
              <TextInput
                value={title}
                onChangeText={setTitle}
                placeholder="Bijv. Vind de oude boom"
                placeholderTextColor={colors.textTertiary}
                className="p-3 rounded-lg"
                style={{
                  backgroundColor: colors.surfaceSecondary,
                  color: colors.text,
                }}
              />
            </View>

            <View className="mb-4">
              <Text className="text-sm font-medium mb-1" style={{ color: colors.text }}>
                Beschrijving
              </Text>
              <TextInput
                value={description}
                onChangeText={setDescription}
                placeholder="Uitleg voor de deelnemers"
                placeholderTextColor={colors.textTertiary}
                multiline
                numberOfLines={3}
                className="p-3 rounded-lg"
                style={{
                  backgroundColor: colors.surfaceSecondary,
                  color: colors.text,
                  minHeight: 80,
                  textAlignVertical: "top",
                }}
              />
            </View>

            <View>
              <Text className="text-sm font-medium mb-1" style={{ color: colors.text }}>
                Hint (optioneel)
              </Text>
              <TextInput
                value={hint}
                onChangeText={setHint}
                placeholder="Extra hulp voor deelnemers"
                placeholderTextColor={colors.textTertiary}
                className="p-3 rounded-lg"
                style={{
                  backgroundColor: colors.surfaceSecondary,
                  color: colors.text,
                }}
              />
            </View>
          </View>

          {/* Step Type Specific Config */}
          <Text
            className="text-sm font-semibold mb-2 uppercase"
            style={{ color: colors.textSecondary }}
          >
            {STEP_TYPE_CONFIG[stepType].label} Configuratie
          </Text>
          <View
            className="rounded-xl p-4 mb-6"
            style={{ backgroundColor: colors.surface }}
          >
            {stepType === "location" && (
              <>
                <View className="mb-4">
                  <Text className="text-sm font-medium mb-1" style={{ color: colors.text }}>
                    Adres
                  </Text>
                  <TextInput
                    value={locationAddress}
                    onChangeText={setLocationAddress}
                    placeholder="Bijv. Hoofdstraat 1, Amsterdam"
                    placeholderTextColor={colors.textTertiary}
                    className="p-3 rounded-lg"
                    style={{
                      backgroundColor: colors.surfaceSecondary,
                      color: colors.text,
                    }}
                  />
                </View>

                <Text
                  className="text-center text-sm mb-4"
                  style={{ color: colors.textSecondary }}
                >
                  — of —
                </Text>

                <View className="flex-row mb-4">
                  <View className="flex-1 mr-2">
                    <Text className="text-sm font-medium mb-1" style={{ color: colors.text }}>
                      Latitude
                    </Text>
                    <TextInput
                      value={locationLatitude}
                      onChangeText={setLocationLatitude}
                      placeholder="52.3676"
                      placeholderTextColor={colors.textTertiary}
                      keyboardType="decimal-pad"
                      className="p-3 rounded-lg"
                      style={{
                        backgroundColor: colors.surfaceSecondary,
                        color: colors.text,
                      }}
                    />
                  </View>
                  <View className="flex-1 ml-2">
                    <Text className="text-sm font-medium mb-1" style={{ color: colors.text }}>
                      Longitude
                    </Text>
                    <TextInput
                      value={locationLongitude}
                      onChangeText={setLocationLongitude}
                      placeholder="4.9041"
                      placeholderTextColor={colors.textTertiary}
                      keyboardType="decimal-pad"
                      className="p-3 rounded-lg"
                      style={{
                        backgroundColor: colors.surfaceSecondary,
                        color: colors.text,
                      }}
                    />
                  </View>
                </View>

                <View className="mb-4">
                  <Text className="text-sm font-medium mb-1" style={{ color: colors.text }}>
                    Radius (meters)
                  </Text>
                  <TextInput
                    value={locationRadius}
                    onChangeText={setLocationRadius}
                    placeholder="50"
                    placeholderTextColor={colors.textTertiary}
                    keyboardType="number-pad"
                    className="p-3 rounded-lg"
                    style={{
                      backgroundColor: colors.surfaceSecondary,
                      color: colors.text,
                    }}
                  />
                  <Text className="text-xs mt-1" style={{ color: colors.textTertiary }}>
                    Afstand in meters waarbinnen de locatie geldig is
                  </Text>
                </View>

                <View className="flex-row items-center justify-between">
                  <View className="flex-1">
                    <Text className="font-medium" style={{ color: colors.text }}>
                      Afstand tonen
                    </Text>
                    <Text className="text-sm" style={{ color: colors.textSecondary }}>
                      Toon afstand tot doellocatie
                    </Text>
                  </View>
                  <Switch
                    value={locationShowDistance}
                    onValueChange={setLocationShowDistance}
                    trackColor={{ false: colors.border, true: "#10B98180" }}
                    thumbColor={locationShowDistance ? "#10B981" : colors.surfaceSecondary}
                  />
                </View>
              </>
            )}

            {stepType === "puzzle" && (
              <>
                <View className="mb-4">
                  <Text className="text-sm font-medium mb-1" style={{ color: colors.text }}>
                    Vraag *
                  </Text>
                  <TextInput
                    value={puzzleQuestion}
                    onChangeText={setPuzzleQuestion}
                    placeholder="De puzzel vraag of opdracht"
                    placeholderTextColor={colors.textTertiary}
                    multiline
                    numberOfLines={3}
                    className="p-3 rounded-lg"
                    style={{
                      backgroundColor: colors.surfaceSecondary,
                      color: colors.text,
                      minHeight: 80,
                      textAlignVertical: "top",
                    }}
                  />
                </View>

                <View className="mb-4">
                  <Text className="text-sm font-medium mb-1" style={{ color: colors.text }}>
                    Antwoord *
                  </Text>
                  <TextInput
                    value={puzzleAnswer}
                    onChangeText={setPuzzleAnswer}
                    placeholder="Het juiste antwoord"
                    placeholderTextColor={colors.textTertiary}
                    className="p-3 rounded-lg"
                    style={{
                      backgroundColor: colors.surfaceSecondary,
                      color: colors.text,
                    }}
                  />
                </View>

                <View className="flex-row items-center justify-between">
                  <View className="flex-1">
                    <Text className="font-medium" style={{ color: colors.text }}>
                      Hoofdlettergevoelig
                    </Text>
                    <Text className="text-sm" style={{ color: colors.textSecondary }}>
                      "Antwoord" ≠ "antwoord"
                    </Text>
                  </View>
                  <Switch
                    value={puzzleCaseSensitive}
                    onValueChange={setPuzzleCaseSensitive}
                    trackColor={{ false: colors.border, true: "#10B98180" }}
                    thumbColor={puzzleCaseSensitive ? "#10B981" : colors.surfaceSecondary}
                  />
                </View>
              </>
            )}

            {stepType === "word" && (
              <>
                <View className="mb-4">
                  <Text className="text-sm font-medium mb-1" style={{ color: colors.text }}>
                    Juiste woord/antwoord *
                  </Text>
                  <TextInput
                    value={wordAnswer}
                    onChangeText={setWordAnswer}
                    placeholder="Het woord dat ingevoerd moet worden"
                    placeholderTextColor={colors.textTertiary}
                    className="p-3 rounded-lg"
                    style={{
                      backgroundColor: colors.surfaceSecondary,
                      color: colors.text,
                    }}
                  />
                </View>

                <View className="flex-row items-center justify-between">
                  <View className="flex-1">
                    <Text className="font-medium" style={{ color: colors.text }}>
                      Hoofdlettergevoelig
                    </Text>
                    <Text className="text-sm" style={{ color: colors.textSecondary }}>
                      "Woord" ≠ "woord"
                    </Text>
                  </View>
                  <Switch
                    value={wordCaseSensitive}
                    onValueChange={setWordCaseSensitive}
                    trackColor={{ false: colors.border, true: "#10B98180" }}
                    thumbColor={wordCaseSensitive ? "#10B981" : colors.surfaceSecondary}
                  />
                </View>
              </>
            )}

            {stepType === "photo" && (
              <>
                <View className="mb-4">
                  <Text className="text-sm font-medium mb-1" style={{ color: colors.text }}>
                    Instructies
                  </Text>
                  <TextInput
                    value={photoInstructions}
                    onChangeText={setPhotoInstructions}
                    placeholder="Wat moet op de foto staan?"
                    placeholderTextColor={colors.textTertiary}
                    multiline
                    numberOfLines={3}
                    className="p-3 rounded-lg"
                    style={{
                      backgroundColor: colors.surfaceSecondary,
                      color: colors.text,
                      minHeight: 80,
                      textAlignVertical: "top",
                    }}
                  />
                </View>

                <View>
                  <Text className="text-sm font-medium mb-1" style={{ color: colors.text }}>
                    Minimum aantal foto's
                  </Text>
                  <TextInput
                    value={photoMinCount}
                    onChangeText={setPhotoMinCount}
                    placeholder="1"
                    placeholderTextColor={colors.textTertiary}
                    keyboardType="number-pad"
                    className="p-3 rounded-lg"
                    style={{
                      backgroundColor: colors.surfaceSecondary,
                      color: colors.text,
                    }}
                  />
                </View>
              </>
            )}

            {stepType === "qr_code" && (
              <View>
                <Text className="text-sm font-medium mb-1" style={{ color: colors.text }}>
                  QR Code waarde *
                </Text>
                <TextInput
                  value={qrCodeValue}
                  onChangeText={setQrCodeValue}
                  placeholder="De waarde die in de QR code staat"
                  placeholderTextColor={colors.textTertiary}
                  className="p-3 rounded-lg"
                  style={{
                    backgroundColor: colors.surfaceSecondary,
                    color: colors.text,
                  }}
                />
                <Text className="text-xs mt-1" style={{ color: colors.textTertiary }}>
                  Dit is de tekst die de QR code scanner moet herkennen
                </Text>
              </View>
            )}
          </View>

          {/* Animation */}
          <Text
            className="text-sm font-semibold mb-2 uppercase"
            style={{ color: colors.textSecondary }}
          >
            Animatie
          </Text>
          <View
            className="rounded-xl p-4 mb-6"
            style={{ backgroundColor: colors.surface }}
          >
            <LottiePicker
              value={completionLottie}
              onChange={setCompletionLottie}
              label="Voltooiings animatie (optioneel)"
              placeholder="Kies een animatie"
            />
          </View>

          <View className="h-32" />
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

